import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CATEGORY_COLORS, DRAWER_STACK, INVOICE_SUBTYPES, INVOICE_TYPES, ROUTES } from 'Utils/constants';
import InvoiceTitle from 'Components/Atoms/InvoiceTitle/InvoiceTitle';
import { Fill } from 'nano-slots';
import ContactEditorDrawer from 'Components/Organisms/ContactEditorDrawer/ContactEditorDrawer';
import InvoiceQuickEditorDrawerPreview from './InvoiceQuickEditorDrawerPreview';
import Tabs, { Tab } from 'Components/Molecules/Tabs/Tabs';
import useDrawerStackRegister from 'Hooks/useDrawerStackRegister';
import TagEditorDrawer from 'Components/Organisms/TagEditorDrawer/TagEditorDrawer';
import useInvoice from 'Hooks/useInvoice';
import useContact from 'Hooks/useContact';
import Loading from 'Components/Atoms/Loading/Loading';
import SelectProduct from 'Components/Organisms/SelectProduct/SelectProduct';
import MultiselectTags from 'Components/Organisms/MultiselectTags/MultiselectTags';
import { createCategory } from 'Services/categories';
import { useQueryClient } from '@tanstack/react-query';
import Textarea from 'Components/Atoms/Textarea/Textarea';
import DatePicker from 'Components/Atoms/DatePicker/DatePicker';
import { formatDateISO } from 'Utils/formatters';
import DrawerStackSavingIndicator from 'Components/Organisms/DrawerStackProvider/DrawerStackSavingIndicator';
import CardInvoice from 'Components/Molecules/CardInvoice/CardInvoice';
import Button from 'Components/Atoms/Button/Button';
import { useNavigate } from 'react-router-dom';
import OutlineEdit from 'Components/Atoms/OutlineIcons/OutlineEdit';
import OutlineDownload from 'Components/Atoms/OutlineIcons/OutlineDownload';
import Icon from 'Components/Atoms/Icon/Icon';
import useDrawerStack from 'Hooks/useDrawerStack';
import useMediaQuery from 'Hooks/useMediaQuery';
import InvoiceQuickEditorDrawerConciliation from './InvoiceQuickEditorDrawerConciliation';
import Switch from 'Components/Atoms/Switch/Switch';
import { convertInvoiceToDraft } from 'Services/invoices';
import InvoiceDuplicateWarning from 'Components/Molecules/InvoiceDuplicateWarning/InvoiceDuplicateWarning';
import InvoiceQuickEditorDrawerRelated from './InvoiceQuickEditorDrawerRelated';
import InvoiceCancelDrawer from '../InvoiceCancelDrawer/InvoiceCancelDrawer';
import InvoiceUploadERPDrawer from '../InvoiceUploadERPDrawer/InvoiceUploadERPDrawer';
import ActionsMenu from 'Components/Molecules/ActionsMenu/ActionsMenu';
import DrawerStackConciliatedBadge from 'Components/Organisms/DrawerStackProvider/DrawerStackConciliatedBadge';
import InvoiceStatusBadge from 'Components/Molecules/InvoiceStatusBadge/InvoiceStatusBadge';
import { useSnackbar } from 'Components/Molecules/SnackbarProvider/SnackbarProvider';

const DESCRIPTION_MAX_LENGTH = 250;

const TABS = {
    DATA: 'DATA',
    CONCILIATION: 'CONCILIATION',
    RELATED: 'RELATED',
};

function validateDate(dateString) {
    if (!dateString) return false;

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return false;

    const currentYear = new Date().getFullYear();
    const year = date.getFullYear();

    if (year < 1900 || year > currentYear + 100) {
        return false;
    }

    return true;
}

const useDebounceCallback = (callback, delay) => {
    const timerRef = useRef();

    const debouncedCallback = useCallback(
        (...args) => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                callback(...args);
            }, delay);
        },
        [delay],
    );

    return debouncedCallback;
};

const download = (url, fileName) => {
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const getFileName = (string) => {
    try {
        const url = new URL(string);
        const path = url.pathname;
        const fileName = path.split('/').pop();
        return fileName;
    } catch (error) {
        return '';
    }
};

export default function InvoiceQuickEditorDrawer(props) {
    const { invoiceId, onClose, onError } = props;
    const { t } = useTranslation('invoices');
    const [toolTab, setToolTab] = useState(TABS.DATA);
    const isMobile = useMediaQuery('(max-width: 767px)');

    const { updateInvoice: updateInvoiceMutate, invoice, isLoading, isUpdating } = useInvoice(invoiceId);
    const { contact } = useContact(invoice?.invoice_party_id);
    const { enqueueSnackbar } = useSnackbar();

    const updateInvoice = (data) =>
        updateInvoiceMutate(data, {
            onError: () => {
                enqueueSnackbar(t('error-updating-invoice-title'), {
                    variant: 'error',
                    description: t('error-updating-invoice-description'),
                });
            },
        });

    const [datePayment, setDatePayment] = useState(() =>
        invoice?.date_payment ? formatDateISO(invoice.date_payment) : '',
    );
    const [description, setDescription] = useState(() => invoice?.description || '');
    const [isAccountingDelivered, setIsAccountingDelivered] = useState(invoice?.is_accounting_delivered || false);
    const navigate = useNavigate();

    const debounceUpdateInvoice = useDebounceCallback(updateInvoice, 500);

    useEffect(() => {
        if (isLoading) return;

        setDatePayment(invoice?.date_payment ? formatDateISO(invoice.date_payment) : '');
        setDescription(invoice?.description || '');
        setIsAccountingDelivered(invoice?.is_accounting_delivered || false);
    }, [isLoading]);

    const queryClient = useQueryClient();
    const { open } = useDrawerStack();

    useDrawerStackRegister([
        [
            DRAWER_STACK.CONTACT_EDITOR_WITH_INVOICE_PREVIEW,
            (props) => {
                return (
                    <>
                        <Fill name="drawer-stack-preview-desktop">
                            <InvoiceQuickEditorDrawerPreview id={invoiceId} />
                        </Fill>

                        <ContactEditorDrawer {...props} />
                    </>
                );
            },
        ],
        [
            DRAWER_STACK.TAG_EDITOR_WITH_INVOICE_PREVIEW,
            (props) => {
                return (
                    <>
                        <Fill name="drawer-stack-preview-desktop">
                            <InvoiceQuickEditorDrawerPreview id={invoiceId} />
                        </Fill>

                        <TagEditorDrawer {...props} />
                    </>
                );
            },
        ],
        [
            DRAWER_STACK.INVOICE_CANCEL_WITH_PREVIEW,
            (props) => {
                return (
                    <>
                        <Fill name="drawer-stack-preview-desktop">
                            <InvoiceQuickEditorDrawerPreview id={invoiceId} />
                        </Fill>

                        <InvoiceCancelDrawer {...props} />
                    </>
                );
            },
        ],
        [
            DRAWER_STACK.INVOICE_UPLOAD_ERP,
            (props) => {
                return (
                    <>
                        <Fill name="drawer-stack-preview-desktop">
                            <InvoiceQuickEditorDrawerPreview id={invoiceId} />
                        </Fill>

                        <InvoiceUploadERPDrawer {...props} />
                    </>
                );
            },
        ],
    ]);

    const handleAddTag = () => {
        const randomColor = Math.floor(Math.random() * CATEGORY_COLORS.length);
        const color = CATEGORY_COLORS[randomColor];

        open(DRAWER_STACK.TAG_EDITOR_WITH_INVOICE_PREVIEW, {
            title: t('create-tag'),
            tag: {
                name: '',
                color,
                icon: '💰',
            },
            onSubmit: async (data) => {
                const tag = await createCategory(data);
                await queryClient.invalidateQueries({ queryKey: ['categories'] });
                if (tag) {
                    updateInvoice({ tags_ids: [...(invoice?.tags_ids || []), tag.id] });
                }
            },
        });
    };

    const handleDownload = () => {
        if (!invoice?.file_url) return;

        const fileName = getFileName(invoice.file_url);
        download(invoice.file_url, fileName);
    };

    const handleSend = () => {
        open(DRAWER_STACK.INVOICE_SEND, {
            title: t('send-title'),
            invoiceId,
            onSubmit: async () => {
                onClose();
            },
        });
    };

    const handleDelete = () => {
        open(DRAWER_STACK.INVOICE_DELETE, {
            invoiceId,
            onSubmit: async () => {
                onClose();
            },
        });
    };

    const handleEdit = () => {
        onClose();
        navigate(`${ROUTES.invoicesEdit}/${invoice.id}`);
    };

    const handleViewOriginalInvoice = () => {
        open(DRAWER_STACK.INVOICE_QUICK_EDITOR, {
            invoiceId: invoice?.invoice_duplicated_id,
            title: <InvoiceTitle invoice={invoice} />,
        });
    };

    const handleCancelInvoice = () => {
        open(DRAWER_STACK.INVOICE_CANCEL_WITH_PREVIEW, {
            invoice,
            onSubmit: async () => {
                onClose();
            },
        });
    };

    const handleDuplicateInvoice = () => {
        navigate(ROUTES.invoicesIssue, {
            state: { invoice },
        });
        onClose();
    };

    const handleIssueCorrection = () => {
        const correctiveSubtype =
            invoice.subtype === INVOICE_SUBTYPES.SIMPLIFIED
                ? INVOICE_SUBTYPES.SIMPLIFIED_CORRECTIVE
                : INVOICE_SUBTYPES.STANDARD_CORRECTIVE;

        navigate(ROUTES.invoicesIssue, {
            state: {
                invoice: {
                    ...invoice,
                    subtype: correctiveSubtype,
                    invoice_corrected_id: invoice.id,
                    identifier: null,
                    serie_id: '',
                    correction_type: 'I',
                },
            },
        });
        onClose();
    };

    const handleUploadToERP = () => {
        open(DRAWER_STACK.INVOICE_UPLOAD_ERP, {
            title: t('upload-to-erp'),
            invoice,
            onClose: () => {},
        });
    };

    const handleConvertToDraft = async () => {
        try {
            await convertInvoiceToDraft(invoice.id);
            await queryClient.invalidateQueries({ queryKey: ['invoices'] });
            onClose();
            navigate(`${ROUTES.invoicesEdit}/${invoice.id}`);
        } catch (error) {
            onError?.(error);
        }
    };

    const isCancelable = invoice?.is_cancelable;
    const isConvertibleToDraft = invoice?.type === INVOICE_TYPES.SALE && invoice?.is_imported;
    const isCorrectable =
        invoice?.type === INVOICE_TYPES.SALE &&
        [INVOICE_SUBTYPES.STANDARD, INVOICE_SUBTYPES.SIMPLIFIED].includes(invoice?.subtype);

    const showStatusBadge =
        invoice?.subtype &&
        invoice?.tax_authority_provider_status !== 'cancelled' &&
        ![INVOICE_SUBTYPES.QUOTE, INVOICE_SUBTYPES.PROFORMA, INVOICE_SUBTYPES.DELIVERY_NOTE].includes(invoice.subtype);

    return (
        <>
            <DrawerStackSavingIndicator saving={isUpdating} />

            {showStatusBadge && (
                <DrawerStackConciliatedBadge>
                    <InvoiceStatusBadge
                        status={invoice.reconciled_status}
                        expirationDate={invoice.date_expiration}
                        type={invoice.type}
                        subtype={invoice.subtype}
                    />
                </DrawerStackConciliatedBadge>
            )}

            <Fill name="drawer-stack-preview-desktop">
                <InvoiceQuickEditorDrawerPreview id={invoiceId} />
            </Fill>
            <Fill name="drawer-stack-preview-mobile">
                <div className="px-4 pb-3">
                    <InvoiceQuickEditorDrawerPreview id={invoiceId} compactMode />
                </div>
            </Fill>

            {isLoading && (
                <div className="flex justify-center py-6">
                    <Loading />
                </div>
            )}

            {!isLoading && (
                <div className="h-full flex flex-col">
                    <InvoiceDuplicateWarning
                        invoice={invoice}
                        onDelete={handleDelete}
                        onViewOriginal={handleViewOriginalInvoice}
                        onError={onError}
                    />

                    <div className="pb-4 pt-2">
                        <CardInvoice invoice={invoice} />
                    </div>
                    <div className="flex flex-wrap gap-2 items-center mb-4">
                        {invoice?.type === INVOICE_TYPES.SALE && (
                            <Button variant="primary" size="extraSmall" className="grow px-1.5" onClick={handleSend}>
                                <Icon name="send" size={16} color="currentColor" />
                                {t('send')}
                            </Button>
                        )}

                        <Button size="extraSmall" className="grow px-1.5" onClick={handleEdit}>
                            <OutlineEdit size={18} />
                            {t('edit')}
                        </Button>

                        <Button
                            size="extraSmall"
                            disabled={!invoice?.file_url}
                            className="grow px-1.5 disabled:opacity-50"
                            onClick={handleDownload}
                        >
                            <OutlineDownload size={18} />
                            {t('download')}
                        </Button>

                        {!isMobile && (
                            <Button size="extraSmall" className="grow px-1.5" onClick={handleDuplicateInvoice}>
                                <Icon name="copy-clipboard" size={16} color="currentColor" />
                                {t('duplicate-invoice')}
                            </Button>
                        )}

                        <ActionsMenu
                            className="bg-white border px-1 h-8 shrink-0 border-gray-200 shadow-sm hover:bg-gray-50 text-dark-blue"
                            disablePortal
                            actions={[
                                ...(isMobile
                                    ? [
                                          {
                                              label: t('duplicate-invoice'),
                                              onClick: handleDuplicateInvoice,
                                          },
                                      ]
                                    : []),
                                ...(contact?.integration_id && !invoice?.integration_id
                                    ? [
                                          {
                                              label: t('upload-to-erp'),
                                              onClick: handleUploadToERP,
                                          },
                                      ]
                                    : []),
                                ...(isConvertibleToDraft
                                    ? [
                                          {
                                              label: t('convert-to-draft'),
                                              onClick: handleConvertToDraft,
                                          },
                                      ]
                                    : []),
                                ...(isCorrectable
                                    ? [
                                          {
                                              label: t('issue-correction'),
                                              onClick: handleIssueCorrection,
                                          },
                                      ]
                                    : []),
                                ...(isCancelable
                                    ? [
                                          {
                                              label: t('cancel-invoice'),
                                              onClick: handleCancelInvoice,
                                              disabled: invoice?.transactions_associated?.length > 0,
                                              disabledReason: t('cancel-invoice-has-transactions'),
                                          },
                                      ]
                                    : []),
                                {
                                    label: t('delete'),
                                    onClick: handleDelete,
                                    disabled: !invoice?.is_deletable,
                                    disabledReason: t('once_issued_this_invoice_cannot_be_deleted'),
                                },
                            ]}
                        />
                    </div>
                    <div className="pb-4 overflow-x-auto overflow-y-hidden shrink-0">
                        <Tabs value={toolTab} onChange={setToolTab} className="min-w-full">
                            <Tab value={TABS.DATA}>{t('data')}</Tab>
                            <Tab value={TABS.CONCILIATION}>{t('transactions')}</Tab>
                            <Tab value={TABS.RELATED}>{t('related-invoices')}</Tab>
                        </Tabs>
                    </div>

                    {toolTab === TABS.DATA && (
                        <div className="grid gap-6 pt-3">
                            <SelectProduct
                                value={invoice?.product_id ?? ''}
                                onChange={(productId) => updateInvoice({ product_id: productId ? productId : null })}
                                label={
                                    invoice?.type === INVOICE_TYPES.PURCHASE
                                        ? t('payment-account')
                                        : t('collection-account')
                                }
                            />

                            <MultiselectTags
                                label={t('tags')}
                                value={invoice?.tags_ids}
                                onChange={(tagIds) => updateInvoice({ tags_ids: tagIds })}
                                onAdd={handleAddTag}
                            />

                            <Textarea
                                label={t('internal-note')}
                                placeholder={t('invoice-description-placeholder')}
                                value={description ?? ''}
                                maxLength={DESCRIPTION_MAX_LENGTH}
                                onChange={(e) => {
                                    // The native maxLength is not enforced when the value is set programmatically
                                    const value = e.target.value.slice(0, DESCRIPTION_MAX_LENGTH);
                                    setDescription(value);
                                    debounceUpdateInvoice({
                                        description: value === '' ? null : value,
                                    });
                                }}
                            />
                            <DatePicker
                                name="date_payment"
                                label={
                                    invoice?.reconciled_status === 'RECONCILED'
                                        ? t('payment-date')
                                        : t('payment-date-expected')
                                }
                                value={datePayment}
                                onChange={(e) => {
                                    const date = e.target.value;
                                    setDatePayment(date);

                                    const isValid = validateDate(date);
                                    debounceUpdateInvoice({ date_payment: isValid ? date : null });
                                }}
                            />

                            <div className="flex justify-content-start gap-2 items-center">
                                <Switch
                                    checked={isAccountingDelivered}
                                    onCheckedChange={(ev) => {
                                        setIsAccountingDelivered(ev);
                                        debounceUpdateInvoice({ is_accounting_delivered: ev });
                                    }}
                                />
                                <span>{t('accounting-delivered')}</span>
                            </div>
                        </div>
                    )}

                    {toolTab === TABS.CONCILIATION && <InvoiceQuickEditorDrawerConciliation id={invoiceId} />}

                    {toolTab === TABS.RELATED && <InvoiceQuickEditorDrawerRelated id={invoiceId} />}
                </div>
            )}
        </>
    );
}

InvoiceQuickEditorDrawer.propTypes = {
    invoiceId: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    onError: PropTypes.func,
};
